import { mappingConfig } from "./mappingConfig";
import { sideMappingConfig } from "./sideMappingConfig";

/**
 * Photo mapping utility for geodesic dome template.
 *
 * Maps an uploaded photo onto the dome shape net by:
 * 1. Using top-view triangle geometry as a "cookie cutter" on the photo
 * 2. Computing affine transforms from top-view → shape-net coordinates
 * 3. Generating SVG clipPath + image elements for each triangle
 */

// ---------------------------------------------------------------------------
// Parse "M x1 y1 L x2 y2 L x3 y3 z" style SVG path into 3 vertices
// Also handles "M x1 y1 l dx2 dy2 dx3 dy3 z" (relative coords)
// ---------------------------------------------------------------------------
function parsePath(d) {
  // Regex matches: letter OR number (including decimals, negatives, and exponents)
  // This correctly splits concatenated numbers like "-34.952.605" -> "-34.952", ".605"
  const tokens = d.match(/[A-Za-z]|-?\d*\.?\d+(?:[eE][-+]?\d+)?/g);
  if (!tokens) return [];

  const vertices = [];
  let curX = 0, curY = 0;
  let i = 0;
  let activeCmd = null;
  
  while (i < tokens.length) {
    const token = tokens[i];
    if (/^[A-Za-z]$/.test(token)) {
      activeCmd = token;
      i++;
      if (activeCmd === 'Z' || activeCmd === 'z') break;
    }
    
    // Process coordinates until next command
    while (i < tokens.length && !/^[A-Za-z]$/.test(tokens[i])) {
      if (activeCmd === 'h' || activeCmd === 'H' || activeCmd === 'v' || activeCmd === 'V') {
        const val = parseFloat(tokens[i++]);
        if (activeCmd === 'h') curX += val;
        else if (activeCmd === 'H') curX = val;
        else if (activeCmd === 'v') curY += val;
        else if (activeCmd === 'V') curY = val;
      } else {
        let x = parseFloat(tokens[i++]);
        let yStr = tokens[i++];
        if (yStr === undefined) break; // Should not happen in valid triangle paths
        let y = parseFloat(yStr);
        
        if (activeCmd === 'm' || activeCmd === 'l') {
          curX += x;
          curY += y;
        } else if (activeCmd === 'M' || activeCmd === 'L') {
          curX = x;
          curY = y;
        }
      }
      
      vertices.push([curX, curY]);

      // Implicit command change: after first M/m pair, subsequent pairs are lines
      if (activeCmd === 'M') activeCmd = 'L';
      if (activeCmd === 'm') activeCmd = 'l';
    }
  }
  return vertices.slice(0, 3);
}

// ---------------------------------------------------------------------------
// Shape Net triangle coordinates (from the SVG path d attributes)
// These are in the local coordinate space of the SHAPE_NET <g> group.
// The group itself has transform: scale(0.97) translate(3px, 5px)
// which is applied by the SVG renderer.
// ---------------------------------------------------------------------------
// We store raw d-strings and parse on demand.
const shapeNetPaths = {
  // Section 1
  s1t1: "M108.79 154.88l22.928 32.217-34.952.605z",
  s1t2: "M108.788 154.878l39.387-3.738-16.457 35.957z",
  s1t3: "M148.175 151.14l-39.367 3.736 16.96-30.565z",
  s1t4: "M131.719 187.094l16.456-35.954 17.985 29.974z",
  s1t5: "M148.175 151.14l39.472 2.382-21.471 27.583z",
  s1t6: "M187.667 153.528l-39.492-2.388 21.812-32.984z",
  s1t7: "M169.98 118.154l-21.805 32.986-13.148-32.388z",
  s1t8: "M147.11 85.895l22.87 32.26-34.954.54z",

  // Section 2
  s2t1: "M65.226 172.703l34.198-19.852-2.629 34.859z",
  s2t2: "M65.223 172.705l-.08-39.565 34.281 19.712z",
  s2t3: "M65.143 133.14l.08 39.544-28.866-19.712z",
  s2t4: "M99.421 152.85l-34.278-19.71 31.508-15.137z",
  s2t5: "M65.143 133.14l6.02-39.083 25.48 23.93z",
  s2t6: "M71.172 94.038l-6.029 39.102-30.827-24.768z",
  s2t7: "M34.314 108.38l30.829 24.76-33.465 10.098z",
  s2t8: "M.079 128.169l34.235-19.789-2.694 34.853z",

  // Section 3
  s3t1: "M71.397 211.69l-6.137-39.063 31.547 15.06z",
  s3t2: "M71.398 211.694l-36.914-14.237 30.776-24.83z",
  s3t3: "M34.484 197.457l36.895 14.23-28.819 19.78z",
  s3t4: "M65.258 172.63l-30.774 24.827-2.715-34.85z",
  s3t5: "M34.484 197.457L.225 177.707l31.526-15.098z",
  s3t6: "M.21 177.693l34.274 19.764-34.242 19.78z",
  s3t7: "M.249 217.243l34.235-19.786-2.691 34.852z",
  s3t8: "M6.314 256.317L.249 217.243l31.519 15.118z",

  // Section 4
  s4t1: "M109.926 220.06L71.3 211.59l25.49-23.922z",
  s4t2: "M109.929 220.061l-26.672 29.223-11.956-37.694z",
  s4t3: "M71.302 211.593l11.955 37.69-33.456-10.127z",
  s4t4: "M83.257 249.284l26.658-29.208 7.963 34.036z",
  s4t5: "M83.257 249.284L52.413 274.03l-2.617-34.857z",
  s4t6: "M52.394 274.039l30.863-24.755 5.994 39.087z",
  s4t7: "M89.257 288.367l-6-39.083 31.494 15.165z",
  s4t8: "M127.866 296.908l-38.609-8.54 25.534-23.876z",

  // Section 5
  s5t1: "M131.716 187.082l-21.806 32.986-13.143-32.392z",
  s5t2: "M131.718 187.08l17.662 35.402-39.47-2.414z",
  s5t3: "M149.38 222.482l-17.653-35.384 34.62 4.822z",
  s5t4: "M109.913 220.069l39.467 2.413-21.492 27.568z",
  s5t5: "M149.38 222.482l11.984 37.684-33.462-10.105z",
  s5t6: "M161.366 260.187l-11.986-37.705 38.625 8.48z",
  s5t7: "M188.003 230.953l-38.623-8.47 25.486-23.924z",
  s5t8: "M209.87 198.007l-21.867 32.946-13.083-32.416z",
};
const sidePaths = {
  "side-s3t7": "M117.866 217.281l-13.768-7.236.827 31.98z",
  "side-s3t3": "M117.866 217.281l-13.768-7.236 25.997-18.044z",
  "side-s3t2": "M122.428 189.437l-18.33 20.608 25.997-18.044z",
  "side-s3t1": "M122.428 189.437l26.188-9.595L130.095 192z",
  "side-s3t6": "M96.786 235.686l7.312-25.641.827 31.98z",
  "side-s3t8": "M104.925 242.025l12.94-24.744 12.818 28.945z",
  // Section 5
  "side-s5t5": "M192.595 209.73l-.726 32.406-12.458-24.809z",
  "side-s5t6": "M192.595 209.73l-.726 32.406 7.992-6.718z",
  "side-s5t2": "M174.976 189.437l17.68 20.45-25.83-17.572z",
  "side-s5t1": "M174.976 189.437l-26.367-9.595 18.218 12.473z",
  "side-s5t3": "M192.556 210.073l-13.114 7.366-12.615-25.124z",
  // Section 4
  "side-s4t2": "M166.827 192.315L130.095 192l18.405 22.524z",
  "side-s4t8": "M191.87 242.136l-12.459-24.809-12.93 28.9z",
  "side-s4t3": "M148.548 214.45l30.894 2.989-12.615-25.124z",
  "side-s4t5": "M117.866 217.281l12.817 28.945 17.817-31.888z",
  "side-s4t6": "M166.317 246.226h-35.634l17.817-31.888z",
  "side-s4t7": "M166.317 246.226l13.236-29.177-31.053-2.71z",
  "side-s4t1": "M166.827 192.315L130.095 192l18.521-12.158z",
  "side-s4t4": "M117.866 217.281l12.229-25.28 18.405 22.523z",
};

// ---------------------------------------------------------------------------
// Top View triangle coordinates (from the SVG path d attributes)
// These are in the local coordinate space of the topView <g> group.
// The group has transform="rotate(-.99 -129.653 62.084)"
// ---------------------------------------------------------------------------
const topViewPaths = {
  // Section 1
  "top-s1t1": "M54.137 63.368l15.372-4.989-15.357-11.295z",
  "top-s1t2": "M69.583 42.095L54.18 47.073l15.36 11.259z",
  "top-s1t3": "M69.583 42.095L54.18 47.073l.03-11.354z",
  "top-s1t4": "M69.583 42.095l10.768 12.67-10.81 3.567z",
  "top-s1t5": "M69.583 42.095l10.768 12.67-1.193-9.546z",
  "top-s1t6": "M69.583 42.095l-5.844-8.077 15.419 11.2z",
  "top-s1t7": "M69.583 42.095l-5.844-8.077-9.47 1.7z",
  "top-s1t8": "M44.519 34.227l19.22-.21-9.47 1.702z",

  // Section 2
  "top-s2t1": "M54.137 63.368L38.79 58.32l15.36-11.237z",
  "top-s2t2": "M38.748 42.212l15.432 4.86-15.358 11.202z",
  "top-s2t3": "M38.748 42.212l-10.74 12.496 10.814 3.566z",
  "top-s2t4": "M38.748 42.212l15.432 4.86.03-11.353z",
  "top-s2t5": "M44.519 34.227l-5.728 8.067 15.478-6.575z",
  "top-s2t6": "M44.519 34.227l-5.728 8.067-9.82 3.1z",
  "top-s2t7": "M28.082 54.684l10.71-12.39-9.82 3.1z",
  "top-s2t8": "M28.082 54.684l-4.795 8.707 5.684-17.997z",

  // Section 3
  "top-s3t1": "M54.137 63.368L44.62 76.446l-5.798-18.172z",
  "top-s3t2": "M29.073 71.527l15.547 4.92-5.798-18.173z",
  "top-s3t3": "M29.13 71.644l15.49 4.802-6.672 9.103z",
  "top-s3t4": "M29.073 71.527l-1.065-16.82 10.814 3.567z",
  "top-s3t5": "M29.073 71.527l-1.065-16.82-4.807 8.637z",
  "top-s3t6": "M29.073 71.527l.042 9.873-5.914-18.056z",
  "top-s3t7": "M29.13 71.644l-.073 9.698 8.89 4.207z",
  "top-s3t8": "M44.694 92.741l-15.637-11.4 8.89 4.208z",

  // Section 4
  "top-s4t1": "M54.137 63.368L44.62 76.446l19.033.011z",
  "top-s4t2": "M54.137 89.477l-9.517-13.03 19.033.01z",
  "top-s4t3": "M70.34 85.63l-16.102 3.871 9.473-12.986z",
  "top-s4t4": "M54.137 89.477l-9.517-13.03-6.672 9.102z",
  "top-s4t5": "M44.694 92.741l9.544-3.24-16.29-3.952z",
  "top-s4t6": "M44.694 92.741l9.544-3.24 9.24 3.1z",
  "top-s4t7": "M70.34 85.63l-16.102 3.871 9.24 3.1z",
  "top-s4t8": "M70.34 85.63l-6.776 7.018 15.419-11.004z",

  // Section 5
  "top-s5t1": "M54.137 63.368l15.372-4.989-5.856 18.078z",
  "top-s5t2": "M69.525 58.414l9.544 12.962-15.358 5.14z",
  "top-s5t3": "M70.34 85.63l8.729-14.254-15.358 5.14z",
  "top-s5t4": "M69.525 58.414l9.544 12.962 1.254-16.716z",
  "top-s5t5": "M70.34 85.63l8.729-14.254-.086 10.268z",
  "top-s5t6": "M85.03 63.368l-5.961 8.008.03 10.035z",
  "top-s5t7": "M85.03 63.368l-5.961 8.008 1.254-16.716z",
  "top-s5t8": "M85.03 63.368l-5.786-17.985 1.08 9.277z",
};

// ---------------------------------------------------------------------------
// Parse all paths into vertex arrays
// ---------------------------------------------------------------------------
function getShapeNetTriangles() {
  const result = {};
  for (const [key, d] of Object.entries(shapeNetPaths)) {
    result[key] = parsePath(d);
  }
  return result;
}

function getTopViewTriangles() {
  const result = {};
  for (const [key, d] of Object.entries(topViewPaths)) {
    result[key] = parsePath(d);
  }
  return result;
}

function getSideViewTriangles() {
  const result = {};
  for (const [key, d] of Object.entries(sidePaths)) {
    result[key] = parsePath(d);
  }
  return result;
}

// ---------------------------------------------------------------------------
// Compute bounding box of the top-view decagon (all top-view triangles)
// ---------------------------------------------------------------------------
function getTopViewBounds() {
  const triangles = getTopViewTriangles();
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;

  for (const verts of Object.values(triangles)) {
    for (const v of verts) {
      minX = Math.min(minX, v[0]);
      minY = Math.min(minY, v[1]);
      maxX = Math.max(maxX, v[0]);
      maxY = Math.max(maxY, v[1]);
    }
  }

  return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
}

// ---------------------------------------------------------------------------
// Compute affine transform matrix that maps 3 source points to 3 dest points.
// Returns [a, b, c, d, e, f] for SVG matrix(a,b,c,d,e,f)
// ---------------------------------------------------------------------------
function computeAffineTransform(src, dst) {
  const [[x0, y0], [x1, y1], [x2, y2]] = src;
  const [[u0, v0], [u1, v1], [u2, v2]] = dst;

  // Solve the system of linear equations
  const det = x0 * (y1 - y2) - x1 * (y0 - y2) + x2 * (y0 - y1);

  if (Math.abs(det) < 1e-10) {
    // Degenerate triangle, return identity
    return [1, 0, 0, 1, 0, 0];
  }

  const a = (u0 * (y1 - y2) + u1 * (y2 - y0) + u2 * (y0 - y1)) / det;
  const c = (u0 * (x2 - x1) + u1 * (x0 - x2) + u2 * (x1 - x0)) / det;
  const e =
    (u0 * (x1 * y2 - x2 * y1) +
      u1 * (x2 * y0 - x0 * y2) +
      u2 * (x0 * y1 - x1 * y0)) /
    det;

  const b = (v0 * (y1 - y2) + v1 * (y2 - y0) + v2 * (y0 - y1)) / det;
  const d = (v0 * (x2 - x1) + v1 * (x0 - x2) + v2 * (x1 - x0)) / det;
  const f =
    (v0 * (x1 * y2 - x2 * y1) +
      v1 * (x2 * y0 - x0 * y2) +
      v2 * (x0 * y1 - x1 * y0)) /
    det;

  return [a, b, c, d, e, f];
}

// ---------------------------------------------------------------------------
// Compute how to position/scale the image so it covers the top-view decagon.
// Returns { x, y, width, height } for the <image> element in top-view space.
//
// The image is scaled so the decagon fits within it (cover mode), centered.
// ---------------------------------------------------------------------------
function getImagePlacement(imgNaturalWidth, imgNaturalHeight) {
  const bounds = getTopViewBounds();

  const imgAspect = imgNaturalWidth / imgNaturalHeight;
  const decAspect = bounds.width / bounds.height;

  let imgW, imgH;

  if (imgAspect > decAspect) {
    // Image is wider than decagon — fit by height
    imgH = bounds.height;
    imgW = imgH * imgAspect;
  } else {
    // Image is taller — fit by width
    imgW = bounds.width;
    imgH = imgW / imgAspect;
  }

  // Center the image on the decagon center
  const cx = (bounds.minX + bounds.maxX) / 2;
  const cy = (bounds.minY + bounds.maxY) / 2;

  return {
    x: cx - imgW / 2,
    y: cy - imgH / 2,
    width: imgW,
    height: imgH,
  };
}

// ---------------------------------------------------------------------------
// Helpers for manual mapping and orientation
// ---------------------------------------------------------------------------

/**
 * Shift vertices by 0, 1, or 2 positions to fix orientation.
 */
function rotateVertices(verts, count) {
  if (!count) return verts;
  const result = [...verts];
  for (let i = 0; i < count % 3; i++) {
    result.push(result.shift());
  }
  return result;
}

// ---------------------------------------------------------------------------
// Generate the mapping data needed to render photo triangles.
// ---------------------------------------------------------------------------
function generatePhotoMappingData(imgNaturalWidth, imgNaturalHeight) {
  const snTriangles = getShapeNetTriangles();
  const tvTriangles = getTopViewTriangles();
  const imgPlacement = getImagePlacement(imgNaturalWidth, imgNaturalHeight);

  const mappings = [];

  for (const config of mappingConfig) {
    const { snId, tvId, rotation } = config;

    const snV = snTriangles[snId];
    const tvV = tvTriangles[tvId];

    if (!snV || !tvV) continue;

    // Use our manual rotation to align the textures.
    // The user can change the 'rotation' property in mappingConfig.js
    // to cycle through the 3 possible vertex pairings.
    let processedTvV = rotateVertices(tvV, rotation || 0);

    // If mirroring is requested, swap the points to flip the texture
    if (config.mirror) {
      processedTvV = [processedTvV[0], processedTvV[2], processedTvV[1]];
    }

    // Compute affine transform: Source (TV) -> Destination (SN)
    const matrix = computeAffineTransform(processedTvV, snV);

    mappings.push({
      id: snId,
      clipVertices: snV,
      transform: matrix,
      imgPlacement,
    });
  }

  return mappings;
}

// ---------------------------------------------------------------------------
// Generate mapping data for the top view (simpler: image is placed directly)
// ---------------------------------------------------------------------------
function generateTopViewMappingData(imgNaturalWidth, imgNaturalHeight) {
  const topView = getTopViewTriangles();
  const imgPlacement = getImagePlacement(imgNaturalWidth, imgNaturalHeight);

  const mappings = [];

  for (let s = 1; s <= 5; s++) {
    for (let t = 1; t <= 8; t++) {
      const tvKey = `top-s${s}t${t}`;
      const tvVerts = topView[tvKey];
      if (!tvVerts) continue;

      mappings.push({
        id: tvKey,
        clipVertices: tvVerts,
        imgPlacement,
      });
    }
  }

  return mappings;
}

// ---------------------------------------------------------------------------
// Generate mapping data for the side view triangles.
// ---------------------------------------------------------------------------
function generateSideViewMappingData(imgNaturalWidth, imgNaturalHeight) {
  const sideTriangles = getSideViewTriangles();
  const tvTriangles = getTopViewTriangles();
  const imgPlacement = getImagePlacement(imgNaturalWidth, imgNaturalHeight);

  const mappings = [];

  for (const config of sideMappingConfig) {
    const { snId, tvId, rotation } = config;

    const sideV = sideTriangles[snId];
    const tvV = tvTriangles[tvId];

    if (!sideV || !tvV) continue;

    let processedTvV = rotateVertices(tvV, rotation || 0);

    if (config.mirror) {
      processedTvV = [processedTvV[0], processedTvV[2], processedTvV[1]];
    }

    const matrix = computeAffineTransform(processedTvV, sideV);

    mappings.push({
      id: snId,
      clipVertices: sideV,
      transform: matrix,
      imgPlacement,
    });
  }

  return mappings;
}

export {
  generatePhotoMappingData,
  generateTopViewMappingData,
  generateSideViewMappingData,
  getImagePlacement,
  getTopViewBounds,
  parsePath,
};

